import { gql } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import {
  RelationshipBackgroundColorMap,
  RelationshipColorMap,
  RelationshipLabelMap,
  SentimentLabelMap,
} from 'features/socialListeningCommunity';
import {
  CreatorProfileFragmentCommunityDetailsFragment,
  CreatorProfileRelationshipStrength,
  Sentiment,
} from 'graphql/generated';
import { useMemo } from 'react';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

// eslint-disable-next-line
gql`
  fragment CreatorProfileFragmentCommunityDetails on CreatorProfileModel {
    id
    brandMentionCount
    totalComments
    averageSentimentScore
    relationshipStrength
  }
`;

interface CommunityDetailsProps {
  creatorProfileStats: CreatorProfileFragmentCommunityDetailsFragment;
}

export const CommunityDetails = ({
  creatorProfileStats,
}: CommunityDetailsProps) => {
  const relationshipStrength =
    creatorProfileStats.relationshipStrength ||
    CreatorProfileRelationshipStrength.Neutral;

  const sentiment = useMemo(() => {
    const sentimentScore = creatorProfileStats.averageSentimentScore ?? 0;
    if (sentimentScore > 0) return SentimentLabelMap[Sentiment.Positive];
    if (sentimentScore < 0) return SentimentLabelMap[Sentiment.Negative];
    return SentimentLabelMap[Sentiment.Neutral];
  }, [creatorProfileStats]);

  const data = [
    {
      label: 'Brand mentions',
      value: formatBigNumber(creatorProfileStats.brandMentionCount ?? 0),
    },
    {
      label: 'Comments',
      value: formatBigNumber(creatorProfileStats.totalComments ?? 0),
    },
    {
      label: 'Sentiment',
      value: sentiment,
    },
  ];
  return (
    <Box
      sx={{
        background: 'rgba(35, 6, 3, 0.03)',
        borderRadius: 3,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Typography variant="headline-xs" color={theme.colors?.utility[700]}>
        Creator Details
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={4}>
        {data.map((item) => (
          <Box
            key={item.label}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: theme.spacing(2, 3),
              borderRadius: 4,
              background: 'rgba(35, 6, 3, 0.03)',
            }}
          >
            <Typography
              variant="headline-xs"
              color={theme.colors?.utility[700]}
            >
              {item.label}
            </Typography>
            <Typography
              variant="headline-md"
              fontSize={20}
              color={theme.colors?.utility[900]}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: 'fit-content',
        }}
      >
        <Typography variant="headline-xs" color={theme.colors?.utility[700]}>
          Relationship
        </Typography>
        <Box
          sx={{
            background: RelationshipBackgroundColorMap[relationshipStrength],
            borderRadius: 2,
            p: theme.spacing(1, 3),
          }}
        >
          <Typography
            variant="headline-sm"
            color={RelationshipColorMap[relationshipStrength]}
          >
            {RelationshipLabelMap[relationshipStrength]}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
